import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { getUserInformation } from "../../../store/userRole";
import { getSummaryDetails } from "../../../utils/dashboard";
import { getSubjectPerformance } from "../../../utils/scores";
import {
  getAttendedLiveClasses,
  getMissedClasses,
} from "../../../utils/liveClasses";
import { getAttempts, getIeltsAttempts } from "../../../utils/practices";
import { getSingalClassroomData } from "../../../utils/classrooms";
import { getUserById } from "../../../utils/users";
import "./PDFReport.scss";
import {roundToHalf} from "./Diagnostic/utils";

const PDFReport = ({ classroomId, studentId, assessmentData = [], hasIeltsExams = false, setDataLoading = () => {} }) => {
  const userInformation = useSelector(getUserInformation);
  const [classroomData, setClassroomData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [summaryDetails, setSummaryDetails] = useState({});
  const [attendedClasses, setAttendedClasses] = useState([]);
  const [missedClasses, setMissedClasses] = useState([]);
  const [satScores, setSatScores] = useState({
    readingWriting: [],
    math: [],
    combined: [],
    dates: [],
    bands: [],
  });
  const [subjectPerformances, setSubjectPerformances] = useState([]);
  const [currentDate] = useState(
    new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
  );

  // Process SAT scores from assessment attempts
  const processSatScores = useCallback((attempts) => {
    if (!attempts || attempts.length === 0) {
      return {
        readingWriting: [],
        math: [],
        combined: [],
        dates: [],
        bands: [],
      };
    }

    const scores = {
      readingWriting: [],
      math: [],
      combined: [],
      dates: [],
      bands: [],
    };

    // Sort attempts by date in ascending order (oldest to newest)
    const sortedAttempts = [...attempts].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );

    sortedAttempts.forEach((attempt) => {
      if (!attempt?.url && !hasIeltsExams) return;
      if(hasIeltsExams) {
        scores.bands.push(attempt.overallBandScore);
        scores.dates.push(new Date(attempt.createdAt).toLocaleDateString());
      } else {
        try {
          const decodedData = JSON.parse(attempt.url);

          if (decodedData?.readingWritingScore && decodedData?.mathScore) {
            scores.readingWriting.push(decodedData.readingWritingScore);
            scores.math.push(decodedData.mathScore);
            scores.combined.push(decodedData.combinedSATScore);
            scores.dates.push(new Date(attempt.createdAt).toLocaleDateString());
          }
        } catch (error) {
          console.error("Error parsing SAT scores:", error);
        }
      }
    });

    return scores;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all required data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch student information if studentId is available
        if (studentId) {
          const studentResponse = await getUserById(studentId);
          if (studentResponse.ok) {
            // The API returns { user: {...}, enrollments: [...] }
            // We need to access studentResponse.data.user
            const userData = studentResponse.data?.user;
            if (userData) {
              setStudentData(userData);
            }
          }
        }

        // Fetch classroom details
        const classroomResponse = await getSingalClassroomData(classroomId);
        if (classroomResponse?.data?.classRoom) {
          setClassroomData(classroomResponse.data.classRoom);

          const yearStartDate = new Date();
          yearStartDate.setFullYear(yearStartDate.getFullYear() - 1);
          let assessmentResponse = null
          if(hasIeltsExams) {
            assessmentResponse = await getIeltsAttempts(classroomId, yearStartDate.toISOString(), studentId)
          } else {
            assessmentResponse = await getAttempts(
              classroomId,
              yearStartDate.toISOString(),
              "assessment",
              studentId,
            );
          }
          if (assessmentResponse?.ok) {
            const assessmentAttempts =
              assessmentResponse?.data?.examAttempts || [];
            setSatScores(processSatScores(assessmentAttempts));
          }

          // If we don't have student data yet, try to find the student in classroom data
          if (
            !studentData &&
            studentId &&
            classroomResponse.data.classRoom.students
          ) {
            const studentInClassroom =
              classroomResponse.data.classRoom.students.find(
                (student) =>
                  student._id === studentId || student.userId === studentId,
              );
            if (studentInClassroom) {
              setStudentData(studentInClassroom);
            }
          }

          // Fetch subject-wise performance for each subject
          const subjects =
            classroomResponse.data.classRoom.course?.subjects || [];
          const subjectPerformanceResults = await Promise.all(
            subjects.map(async (subject) => {
              const res = await getSubjectPerformance(
                classroomId,
                subject._id,
                studentId,
              );
              return {
                subjectName: subject.name,
                ...res, // or whatever structure your API returns
              };
            }),
          );
          setSubjectPerformances(subjectPerformanceResults);
        }

        // Fetch summary details (works with or without studentId)
        const summaryResponse = await getSummaryDetails(classroomId, studentId);
        if (summaryResponse.ok) {
          setSummaryDetails(summaryResponse.data);
        }

        // Fetch attended classes (works with or without studentId)
        const attendedResponse = await getAttendedLiveClasses(
          classroomId,
          studentId,
        );
        if (attendedResponse?.count) {
          setAttendedClasses(attendedResponse.liveClasses);
        }

        // Fetch missed classes (works with or without studentId)
        const missedResponse = await getMissedClasses(classroomId, studentId);
        if (missedResponse?.count) {
          setMissedClasses(missedResponse.liveClasses);
        }

        // Use passed assessment data instead of fetching
        // if (assessmentData && assessmentData.length > 0) {
        //   setSatScores(processSatScores(assessmentData));
        // } else {
          // Fallback: Fetch assessment attempts for SAT scores (last year) if no data passed

        // }
        setDataLoading(false)
      } catch (error) {
        setDataLoading(false)
        console.error("Error fetching PDF data:", error);
      }
    };

    if (classroomId) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId, studentId, assessmentData]);

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    const totalQuestions = summaryDetails?.totalQ_Answered || 0;
    const correctAnswers = summaryDetails?.totalQ_Correct || 0;
    const accuracy =
      totalQuestions > 0
        ? ((correctAnswers / totalQuestions) * 100).toFixed(1)
        : 0;

    const totalTime =
      (summaryDetails?.timeSpentPractice || 0) +
      (summaryDetails?.timeSpentTest || 0);
    const hours = Math.floor(totalTime / 3600);
    const minutes = Math.floor((totalTime % 3600) / 60);

    const totalAttempts =
      (summaryDetails?.practiceAttempted || 0) +
      (summaryDetails?.testAttempted || 0) +
      (summaryDetails?.assessmentAttempted || 0);

    return {
      accuracy: parseFloat(accuracy),
      totalTime: `${hours}h ${minutes}m`,
      totalAttempts,
      totalQuestions,
      correctAnswers,
      fullLengthExamsAttempted: summaryDetails?.assessmentAttempted || 0,
    };
  }, [summaryDetails]);

  // Use studentData if available, otherwise fall back to userInformation
  const displayStudentInfo = studentData || userInformation;

  return (
    <div className="pdf-report-container">
      {/* Header */}
      <div className="pdf-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-container">
              <img src="/Logo.png" alt="Tutoria Logo" className="header-logo" />
              <span className="logo-text">Tutoria</span>
            </div>
          </div>
          <div className="report-info">
            <h1 className="report-title">Student Performance Report</h1>
            <div className="report-date">Generated on: {currentDate}</div>
          </div>
        </div>
      </div>

      {/* Student Information */}
      <div className="student-info-section">
        <h2 className="section-title mb-2">Student Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Student Name:</label>
            <span>
              {displayStudentInfo?.fullName ||
                displayStudentInfo?.name ||
                displayStudentInfo?.firstName +
                  " " +
                  displayStudentInfo?.lastName ||
                "N/A"}
            </span>
          </div>
          <div className="info-item">
            <label>Class:</label>
            <span>{classroomData?.name || "N/A"}</span>
          </div>
          <div className="info-item">
            <label>Email:</label>
            <span>{displayStudentInfo?.email || "N/A"}</span>
          </div>
          <div className="info-item">
            <label>Course:</label>
            <span>{classroomData?.course?.name || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="performance-summary-section">
        <h2 className="section-title">Performance Summary</h2>
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon">⏱️</div>
            <div className="card-value">{performanceMetrics.totalTime}</div>
            <div className="card-label">Practice & Test Time</div>
          </div>

          <div className="summary-card">
            <div className="card-icon">📊</div>
            <div className="card-value">
              {performanceMetrics.totalQuestions}
            </div>
            <div className="card-label">Questions Answered</div>
          </div>

          <div className="summary-card">
            <div className="card-icon">✅</div>
            <div className="card-value">{attendedClasses.length}</div>
            <div className="card-label">Classes Attended</div>
          </div>

          <div className="summary-card">
            <div className="card-icon">❌</div>
            <div className="card-value">{missedClasses.length}</div>
            <div className="card-label">Classes Missed</div>
          </div>
          <div className="summary-card">
            <div className="card-icon">📝</div>
            <div className="card-value">
              {performanceMetrics.fullLengthExamsAttempted}
            </div>
            <div className="card-label">Exams Attempted</div>
          </div>
        </div>
      </div>

      {/* Subject-wise Performance (at the end) */}
      <div className="subject-performance-section mb-4">
        <h2 className="section-title">Subject-wise Performance</h2>
        {subjectPerformances.length === 0 ? (
          <div className="no-data-message">
            No subject performance data available.
          </div>
        ) : (
          <>
            <div className="subject-performance-table">
              <div className="table-header">
                <div className="header-cell">Subject</div>
                <div className="header-cell">Total Questions Attempted</div>
                <div className="header-cell">Correct</div>
                <div className="header-cell">Incorrect</div>
                {/* Add more columns as needed */}
              </div>
              {subjectPerformances?.filter(perf => !perf?.practiceCount && !perf?.totalPracticePending && !perf?.testCount && !perf?.totalTestPending).map((perf, idx) => (
                <div className="table-row" key={idx}>
                  <div className="table-cell">{perf.subjectName}</div>
                  <div className="table-cell">{perf.attempted ?? "N/A"}</div>
                  <div className="table-cell">{perf.correct ?? "N/A"}</div>
                  <div className="table-cell">{perf.incorrect}</div>
                </div>
              ))}
            </div>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            {hasIeltsExams && (
              <>
                <div className="subject-performance-table">
                  <div className="table-header">
                    <div className="header-cell">Subject</div>
                    <div className="header-cell">Total Practices and Test Sets Attempted</div>
                    <div className="header-cell">Avg. Band</div>
                    <div className="header-cell">Pending</div>
                    {/* Add more columns as needed */}
                  </div>
                  {subjectPerformances?.filter(perf => !!perf?.practiceCount || !!perf?.totalPracticePending || !!perf?.testCount || !!perf?.totalTestPending).map((perf, idx) => (
                    <div className="table-row" key={idx}>
                      <div className="table-cell">{perf.subjectName}</div>
                      <div className="table-cell">{perf.practiceCount + perf.totalPracticePending + perf?.testCount + perf?.totalTestPending ?? "N/A"}</div>
                      <div className="table-cell">{roundToHalf(((perf.totalPracticeGraded + perf?.totalTestGraded) / ((perf?.testCount + perf?.practiceCount) ?? 1))) ?? "N/A"}</div>
                      <div className="table-cell">{perf.totalPracticePending + perf?.totalTestPending}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
          
        )}
      </div>

      {/* Full Length Exams Section */}
      <div className="full-length-exams-section">
        <h2 className="section-title">Full Length Exams</h2>
        <br />
        {!hasIeltsExams ? 
          <>
            {/* Latest Scores Section */}
            {satScores.readingWriting.length > 0 ? (
              <div className="latest-scores-section">
                <h3 className="subsection-titl">Score Analytics</h3>
                <div className="score-cards">
                  <div className="score-card">
                    <div className="score-icon">📚</div>
                    <div className="score-content">
                      <span className="score-label">Reading & Writing</span>
                      <div className="score-values">
                        <div className="latest-score">
                          <span className="score-value">
                            {satScores.readingWriting[
                              satScores.readingWriting.length - 1
                            ] || "N/A"}
                          </span>
                          <span className="score-label-small">Latest</span>
                        </div>
                        <div className="initial-score">
                          <span className="score-value">
                            {satScores.readingWriting[0] || "N/A"}
                          </span>
                          <span className="score-label-small">First Attempt</span>
                        </div>
                      </div>
                      {satScores.readingWriting.length > 1 && (
                        <div className="progress-indicator">
                          <span
                            className={`trend ${
                              satScores.readingWriting[
                                satScores.readingWriting.length - 1
                              ] > satScores.readingWriting[0]
                                ? "up"
                                : "down"
                            }`}
                          >
                            {satScores.readingWriting[
                              satScores.readingWriting.length - 1
                            ] > satScores.readingWriting[0]
                              ? "↑"
                              : "↓"}
                          </span>
                          <span className="progress-value">
                            {Math.abs(
                              satScores.readingWriting[
                                satScores.readingWriting.length - 1
                              ] - satScores.readingWriting[0],
                            )}{" "}
                            points
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="score-card">
                    <div className="score-icon">🔢</div>
                    <div className="score-content">
                      <span className="score-label">Math</span>
                      <div className="score-values">
                        <div className="latest-score">
                          <span className="score-value">
                            {satScores.math[satScores.math.length - 1] || "N/A"}
                          </span>
                          <span className="score-label-small">Latest</span>
                        </div>
                        <div className="initial-score">
                          <span className="score-value">
                            {satScores.math[0] || "N/A"}
                          </span>
                          <span className="score-label-small">First Attempt</span>
                        </div>
                      </div>
                      {satScores.math.length > 1 && (
                        <div className="progress-indicator">
                          <span
                            className={`trend ${
                              satScores.math[satScores.math.length - 1] >
                              satScores.math[0]
                                ? "up"
                                : "down"
                            }`}
                          >
                            {satScores.math[satScores.math.length - 1] >
                            satScores.math[0]
                              ? "↑"
                              : "↓"}
                          </span>
                          <span className="progress-value">
                            {Math.abs(
                              satScores.math[satScores.math.length - 1] -
                                satScores.math[0],
                            )}{" "}
                            points
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="score-card">
                    <div className="score-icon">🎯</div>
                    <div className="score-content">
                      <span className="score-label">Total Score</span>
                      <div className="score-values">
                        <div className="latest-score">
                          <span className="score-value">
                            {satScores.combined[satScores.combined.length - 1] ||
                              "N/A"}
                          </span>
                          <span className="score-label-small">Latest</span>
                        </div>
                        <div className="initial-score">
                          <span className="score-value">
                            {satScores.combined[0] || "N/A"}
                          </span>
                          <span className="score-label-small">First Attempt</span>
                        </div>
                      </div>
                      {satScores.combined.length > 1 && (
                        <div className="progress-indicator">
                          <span
                            className={`trend ${
                              satScores.combined[satScores.combined.length - 1] >
                              satScores.combined[0]
                                ? "up"
                                : "down"
                            }`}
                          >
                            {satScores.combined[satScores.combined.length - 1] >
                            satScores.combined[0]
                              ? "↑"
                              : "↓"}
                          </span>
                          <span className="progress-value">
                            {Math.abs(
                              satScores.combined[satScores.combined.length - 1] -
                                satScores.combined[0],
                            )}{" "}
                            points
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-data-message">
                <div className="no-data-icon">📊</div>
                <div className="no-data-text">No {classroomData?.course?.name} scores available yet</div>
                <div className="no-data-subtext">
                  Complete your first full-length exam to see your scores here
                </div>
              </div>
            )}
          </>
          :
          <></>
        }
      </div>

      {hasIeltsExams ? <>
      {/* All Attempts List */}
      {satScores.dates.length > 0 ? (
        <div className="attempts-list-section">
          <h3 className="subsection-title mb-2">All Attempts</h3>
          <div className="attempts-table">
            <div className="table-header">
              <div className="header-cell">Attempt #</div>
              <div className="header-cell">Date</div>
              <div className="header-cell">Overall Band</div>
            </div>
            {satScores.dates.map((date, index) => (
              <div key={index} className="table-row">
                <div className="table-cell">#{index + 1}</div>
                <div className="table-cell">
                  {new Date(date).toLocaleDateString()}
                </div>
                <div className="table-cell score">
                  {satScores.bands[index] || "N/A"}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="no-data-message">
          <div className="no-data-icon">📋</div>
          <div className="no-data-text">No exam attempts recorded</div>
          <div className="no-data-subtext">
            Your exam attempts will appear here once you complete them
          </div>
        </div>
      )}
      </> : <></>}

      {!hasIeltsExams ? <>
      {/* All Attempts List */}
      {satScores.dates.length > 0 ? (
        <div className="attempts-list-section">
          <h3 className="subsection-title mb-2">All Attempts</h3>
          <div className="attempts-table">
            <div className="table-header">
              <div className="header-cell">Attempt #</div>
              <div className="header-cell">Date</div>
              <div className="header-cell">Reading & Writing</div>
              <div className="header-cell">Math</div>
              <div className="header-cell">Total Score</div>
            </div>
            {satScores.dates.map((date, index) => (
              <div key={index} className="table-row">
                <div className="table-cell">#{index + 1}</div>
                <div className="table-cell">
                  {new Date(date).toLocaleDateString()}
                </div>
                <div className="table-cell score">
                  {satScores.readingWriting[index] || "N/A"}
                </div>
                <div className="table-cell score">
                  {satScores.math[index] || "N/A"}
                </div>
                <div className="table-cell overall-score">
                  {satScores.combined[index] || "N/A"}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="no-data-message">
          <div className="no-data-icon">📋</div>
          <div className="no-data-text">No exam attempts recorded</div>
          <div className="no-data-subtext">
            Your exam attempts will appear here once you complete them
          </div>
        </div>
      )}

      {satScores.dates.length === 0 ? (
        <>
          <br /> <br />
          <br />
        </>
      ) : (
        <></>
      )}
      </>: <></>}

      {hasIeltsExams && (
        <br />
      )}
      {hasIeltsExams && (
        <br />
      )}

      <div className="message-to-contact mt-2">
        <h2 className="section-title">Message to Contact</h2>
        <div className="message-to-contact-content">
          <p>
            If you have any questions or need further assistance, please contact
            admin
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="pdf-footer">
        <div className="footer-content">
          <div className="footer-left">
            <div className="footer-logo-container">
              <img
                src="/Logo.png"
                alt="Tutoria Logo"
                className="footer-logo-img"
              />
              <span className="footer-logo-text">Tutoria</span>
            </div>
          </div>
          <div className="footer-right">
            <div className="footer-date">Report Date: {currentDate}</div>
            <div className="footer-note">
              This report is generated automatically based on student
              performance data
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFReport;
